/* ================================================================
   Autoplay videos — mobile-hardened
   ----------------------------------------------------------------
   Problem: Shopify's video_tag filter already emits muted + playsinline +
   autoplay + loop, but on mobile (iOS Safari + Chrome Android) the browser
   sometimes refuses to start playback on refresh:
     - The video isn't buffered yet when the <video> is mounted, so the
       implicit autoplay attempt fails silently.
     - Data-saver / battery-saver / Low Power Mode blocks autoplay.
     - The page was loaded in a backgrounded tab — autoplay is suspended
       and never retried when the tab gets focus.
     - Shopify's filter sets `muted` as a JS property on some cases but
       not always as an HTML attribute; iOS checks the attribute.

   This script, loaded once globally, does the following for every
   <video autoplay> on the page:
     1. Harden attributes in the DOM (muted, playsinline, webkit-playsinline,
        preload=metadata) — not just the JS properties.
     2. Call .play() explicitly and swallow the Promise rejection.
     3. Use IntersectionObserver to retry play when the video scrolls into
        view, and pause when off-screen (mobile battery).
     4. Retry play on the first user interaction (touchstart, click,
        scroll, keydown) — covers browsers that block autoplay until
        the user has touched the page.
     5. Retry play on visibilitychange when the tab regains focus.
     6. MutationObserver so videos injected later (quickshop, AJAX
        section re-renders, product media swap) get the same treatment.

   Safe on desktop: everything is idempotent and only touches <video>
   elements that have the autoplay attribute, so merchant-placed videos
   expecting user-controlled playback are untouched.
   ================================================================ */

(() => {
  'use strict';

  const MARK = '__spAutoplayManaged';

  const isAutoplay = (v) => v.autoplay || v.hasAttribute('autoplay');

  const hardenAttrs = (v) => {
    // iOS inspects the HTML attributes, not just the JS properties.
    // Set both to cover every path.
    v.muted = true;
    v.defaultMuted = true;
    v.playsInline = true;
    v.loop = v.loop || v.hasAttribute('loop');
    if (!v.hasAttribute('muted')) v.setAttribute('muted', '');
    if (!v.hasAttribute('playsinline')) v.setAttribute('playsinline', '');
    if (!v.hasAttribute('webkit-playsinline')) v.setAttribute('webkit-playsinline', '');
    if (!v.hasAttribute('autoplay')) v.setAttribute('autoplay', '');
    // preload=metadata at minimum so the video is ready to start
    // as soon as it's in view. `none` causes visible-but-not-playing
    // frames on first scroll-into-view on mobile.
    if (!v.preload || v.preload === 'none') v.preload = 'metadata';
  };

  const tryPlay = (v) => {
    if (!isAutoplay(v) || v.ended) return;
    hardenAttrs(v);
    try {
      const p = v.play();
      if (p && typeof p.then === 'function') {
        p.catch(() => {
          /* Autoplay blocked. Retried on visibility / interaction. */
        });
      }
    } catch (_) {
      /* noop — some browsers throw synchronously on early .play(). */
    }
  };

  // IntersectionObserver — primary retry mechanism on mobile.
  // threshold 0.1 fires as soon as 10% of the video is on screen.
  const io =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            for (const e of entries) {
              const v = e.target;
              if (e.isIntersecting) {
                tryPlay(v);
              } else if (!v.paused) {
                // Off-screen: pause to save battery and data.
                // Keeps currentTime so it resumes where it left off.
                try {
                  v.pause();
                } catch (_) {}
              }
            }
          },
          { rootMargin: '0px', threshold: 0.1 }
        )
      : null;

  const manage = (v) => {
    if (v[MARK]) return;
    v[MARK] = true;
    hardenAttrs(v);
    if (io) io.observe(v);
    tryPlay(v);

    // Defensive auto-resume: if the browser pauses the video on its own
    // (tab backgrounded, network blip, decoder stall), re-try once the
    // video is both in view and still flagged autoplay.
    v.addEventListener('pause', () => {
      if (!isAutoplay(v)) return;
      setTimeout(() => {
        if (!v.paused || v.ended) return;
        const r = v.getBoundingClientRect();
        const onScreen =
          r.top < (window.innerHeight || 0) && r.bottom > 0 && r.width > 0 && r.height > 0;
        if (onScreen) tryPlay(v);
      }, 300);
    });
  };

  const collect = (root) => {
    if (!root || !root.querySelectorAll) return;
    const vids = root.querySelectorAll('video');
    for (const v of vids) if (isAutoplay(v)) manage(v);
  };

  const init = () => collect(document);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Retry play across every autoplay video on first user interaction.
  // Chrome Android and some iOS builds gate autoplay until the user
  // has touched the page at least once.
  const retryAll = () => {
    document.querySelectorAll('video').forEach((v) => {
      if (isAutoplay(v) && v.paused && !v.ended) tryPlay(v);
    });
  };
  const interactionEvents = ['touchstart', 'click', 'scroll', 'keydown', 'pointerdown'];
  interactionEvents.forEach((ev) => {
    window.addEventListener(ev, retryAll, { once: true, passive: true, capture: true });
  });

  // Tab refocus — retry. Mobile backgrounds pause decoders aggressively.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') retryAll();
  });

  // Pick up dynamically-added videos (AJAX swaps, modal opens, product
  // media variant changes). No meaningful cost on pages with no videos.
  if ('MutationObserver' in window) {
    const mo = new MutationObserver((muts) => {
      for (const m of muts) {
        for (const n of m.addedNodes) {
          if (n.nodeType !== 1) continue;
          if (n.tagName === 'VIDEO' && isAutoplay(n)) manage(n);
          else collect(n);
        }
      }
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
  }
})();
